import { useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";

const useSessionsAnalytics = (recentVisits = []) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("visitedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Filtered and sorted sessions
  const processedSessions = useMemo(() => {
    let filtered = (recentVisits || []).filter((visit) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        visit.userId?.toLowerCase().includes(searchLower) ||
        visit.name?.toLowerCase().includes(searchLower) ||
        visit.email?.toLowerCase().includes(searchLower)
      );
    });

    // Sort sessions
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue == null) aValue = sortBy === "visitedAt" ? 0 : "";
      if (bValue == null) bValue = sortBy === "visitedAt" ? 0 : "";

      // Determine if numeric/date sort
      const isNumericSort = ["visitedAt"].includes(sortBy);

      if (isNumericSort) {
        const numA = Number(aValue) || 0;
        const numB = Number(bValue) || 0;
        return sortOrder === "asc" ? numA - numB : numB - numA;
      } else {
        // String comparison
        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();
        return sortOrder === "asc"
          ? strA.localeCompare(strB)
          : strB.localeCompare(strA);
      }
    });

    return filtered;
  }, [recentVisits, searchTerm, sortBy, sortOrder]);

  // Paginated sessions
  const paginatedSessions = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return processedSessions.slice(startIndex, startIndex + rowsPerPage);
  }, [processedSessions, page, rowsPerPage]);

  // Handle sort
  const handleSort = (property) => {
    if (sortBy === property) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(property);
      setSortOrder("desc");
    }
    setPage(1);
  };

  // Copy to clipboard
  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(true);
  }, []);

  // Get time ago helper
  const getTimeAgo = useCallback((visitedAt) => {
    if (!visitedAt) return "Unknown";
    return dayjs(visitedAt).fromNow();
  }, []);

  return {
    // State
    searchTerm,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    copyFeedback,
    // Data
    processedSessions,
    paginatedSessions,
    // Handlers
    setSearchTerm: (value) => {
      setSearchTerm(value);
      setPage(1);
    },
    setRowsPerPage: (value) => {
      setRowsPerPage(value);
      setPage(1);
    },
    setPage,
    setCopyFeedback,
    handleSort,
    copyToClipboard,
    getTimeAgo,
  };
};

export default useSessionsAnalytics;
